import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EntityValidations, ISpecificEntityFormProps, ICustomer, Customer, CustomerApp, IEntity } from '../../types';
import { IEntityFormChangeHandlers } from '../../types/EntityFormProps';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { OrbUserContext } from '../OrbUserContext';


class CustomerValidations extends EntityValidations {
    public CustomerRef: string = null;
    public CustomerSectorID: string = null;
    public CustomerCountryID: string = null;
    public EditionID: string = null;
    public OrgConfigID: string = null;
    public PrimaryUserTitle: string = null;
    public PrimaryUserFirstname: string = null;
    public PrimaryUserSurname: string = null;
    public PrimaryUserEmail: string = null;
    public TestServerID: string = null;
}

export const CustomerForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { dataServices, lookupData, loadLookupData: { sectors, customerSectors, customerCountries, editions, servers/*, availableApps*/ } } = useContext(DataContext);

    useMemo(() => sectors(), [sectors]);
    useMemo(() => customerSectors(), [customerSectors]);
    useMemo(() => customerCountries(), [customerCountries]);
    useMemo(() => editions(), [editions]);
    useMemo(() => servers(), [servers]);
    //useMemo(() => availableApps(), [availableApps]);

    const [selectedSystemSectorID, setSelectedSystemSectorID] = useState<number | string>();
    const [selectedSystemCountryID, setSelectedSystemCountryID] = useState<number | string>();
    const [availableApps, setAvailableApps] = useState<IEntity[]>([]);
    const [availableAppsLoaded, setAvailableAppsLoaded] = useState(false);
    const [defOrgConfigs, setDefOrgConfigs] = useState<IEntity[]>([]);
    const [selectedEditionID, setSelectedEditionID] = useState<number | string>();
    const changeHandlersRef = useRef<IEntityFormChangeHandlers<ICustomer>>(null);
    const formStateRef = useRef<any>(null);

    useEffect(() => {

        const fetchAvailableApps = async () => {

            if (
                typeof selectedSystemSectorID === 'number' &&
                selectedSystemSectorID > 0 &&
                typeof selectedSystemCountryID === 'number' &&
                selectedSystemCountryID > 0 &&
                typeof selectedEditionID === 'number' &&
                selectedEditionID > 0
            ) {

                try {

                    const apps = await dataServices.availableAppService.readAvailableAppsWithFilters(
                        selectedEditionID,
                        selectedSystemSectorID,
                        selectedSystemCountryID
                    );

                    setAvailableApps(apps);
                    setAvailableAppsLoaded(true);

                } catch (error) {

                    console.error('Error fetching filtered apps:', error);
                    setAvailableApps([]);
                    setAvailableAppsLoaded(true);

                }

            }
            else {

                setAvailableApps([]);
                setAvailableAppsLoaded(false);

            }

        };

        fetchAvailableApps();

    }, [
        selectedSystemSectorID,
        selectedSystemCountryID,
        selectedEditionID
    ]);

    useEffect(() => {
        if (
            !availableAppsLoaded ||
            !selectedEditionID ||
            !selectedSystemSectorID ||
            !selectedSystemCountryID ||
            !changeHandlersRef.current ||
            !formStateRef.current
        ) {
            return;
        }

        const customer: ICustomer = formStateRef.current.FormData;
        const selectedApps = customer?.CustomerApps ?? [];
        const validAppIDs = new Set(availableApps.map(a => a.ID));
        const filteredCustomerApps = selectedApps.filter(app => validAppIDs.has(app.AppID));

        if (filteredCustomerApps.length !== selectedApps.length) {
            changeHandlersRef.current.changeJson(filteredCustomerApps, 'CustomerApps');
        }
    }, [availableApps, availableAppsLoaded, selectedEditionID, selectedSystemSectorID, selectedSystemCountryID]);

    useEffect(() => {

        const fetchDefOrgConfigs = async () => {

            if (
                typeof selectedSystemSectorID === 'number' && selectedSystemSectorID > 0 &&
                typeof selectedSystemCountryID === 'number' && selectedSystemCountryID > 0 &&
                typeof selectedEditionID === 'number' && selectedEditionID > 0
            ) {
                console.log('Fetching DefOrgConfigMatrix with SectorID:', selectedSystemSectorID, 'CountryID:', selectedSystemCountryID, 'EditionID:', selectedEditionID);
                try {
                    const result = await dataServices.defOrgConfigMatrixService
                        .GetBySectorEditionCountry(
                            selectedSystemSectorID,
                            selectedEditionID,
                            selectedSystemCountryID
                        );

                    setDefOrgConfigs(result);
                    console.log('Fetched DefOrgConfigMatrix:', result);
                }
                catch (error) {
                    console.error('Error fetching DefOrgConfigMatrix:', error);
                    setDefOrgConfigs([]);
                }
            }
            else {
                setDefOrgConfigs([]);
            }
        };

        fetchDefOrgConfigs();

    }, [selectedSystemSectorID, selectedSystemCountryID, selectedEditionID]);


    const validateEntity = (customer: ICustomer): Promise<CustomerValidations> => {
        const errors = new CustomerValidations();

        if (customer.Title === null || customer.Title === '') {
            errors.Title = `Customer name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (customer.CustomerRef === null || customer.CustomerRef === '') {
            errors.CustomerRef = `Customer ref is required`;
            errors.Valid = false;
        }
        else {
            errors.CustomerRef = null;
        }

        if (customer.CustomerRef?.startsWith('_')) {
            errors.CustomerRef = `Customer ref should not start with _`;
            errors.Valid = false;
        }

        if (customer.CustomerSectorID === null) {
            errors.CustomerSectorID = `Customer Sector is required`;
            errors.Valid = false;
        }
        else {
            errors.CustomerSectorID = null;
        }

        if (customer.CustomerCountryID === null) {
            errors.CustomerCountryID = `Customer Country is required`;
            errors.Valid = false;
        }
        else {
            errors.CustomerCountryID = null;
        }

        if (customer.EditionID === null) {
            errors.EditionID = `Edition is required`;
            errors.Valid = false;
        }
        else {
            errors.EditionID = null;
        }

        if (customer.OrgConfigID === null) {
            errors.OrgConfigID = `Org Config is required`;
            errors.Valid = false;
        }
        else {
            errors.OrgConfigID = null;
        }

        if (!customer.PrimaryUserTitle) {
            errors.PrimaryUserTitle = `Primary User Title is required`;
            errors.Valid = false;
        }
        else {
            errors.PrimaryUserTitle = null;
        }

        if (!customer.PrimaryUserFirstname) {
            errors.PrimaryUserFirstname = `Primary User Firstname is required`;
            errors.Valid = false;
        }
        else {
            errors.PrimaryUserFirstname = null;
        }

        if (!customer.PrimaryUserSurname) {
            errors.PrimaryUserSurname = `Primary User Surname is required`;
            errors.Valid = false;
        }
        else {
            errors.PrimaryUserSurname = null;
        }

        if (!customer.PrimaryUserEmail) {
            errors.PrimaryUserEmail = `Primary User Email is required`;
            errors.Valid = false;
        }
        else {
            errors.PrimaryUserEmail = null;
        }

        if (customer.TestServerID === null) {
            errors.TestServerID = `TEST Server is required`;
            errors.Valid = false;
        }
        else {
            errors.TestServerID = null;
        }

        return Promise.resolve(errors);
    };

    return (
        <EntityForm<ICustomer, CustomerValidations>
            {...props}
            entityName='Customer'
            renderFormFields={(changeHandlers, formState) => {
                changeHandlersRef.current = changeHandlers;
                formStateRef.current = formState;
                const { FormData: customer, FormDataBeforeChanges: customerBeforeChanges, ValidationErrors: errors } = formState;

                return (
                    <div>

                        {/* =========================
                            CUSTOMER DETAILS
                        ========================= */}

                        <CrTextField
                            label="Name"
                            maxLength={100}
                            className={styles.formField}
                            required={true}
                            value={customer.Title}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'Title')}
                            errorMessage={errors.Title}
                        />

                        <CrTextField
                            label="Customer Ref"
                            maxLength={100}
                            className={styles.formField}
                            required={true}
                            value={customer.CustomerRef}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'CustomerRef')}
                            errorMessage={errors.CustomerRef}
                        />

                        <CrTextField
                            label="Address"
                            multiline
                            rows={3}
                            maxLength={500}
                            className={styles.formField}
                            value={customer.Address}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'Address')}
                        />

                        <CrTextField
                            label="Website"
                            maxLength={1000}
                            className={styles.formField}
                            value={customer.Website}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'Website')}
                        />

                        <CrTextField
                            label="Organisation Phone"
                            maxLength={50}
                            className={styles.formField}
                            value={customer.OrgPhone}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'OrgPhone')}
                        />

                        <CrTextField
                            label="Number Of Staff"
                            maxLength={50}
                            className={styles.formField}
                            value={customer.NumberOfStaff}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'NumberOfStaff')}
                        />

                        <CrDropdown
                            label='Customer Sector'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.CustomerSectors)}
                            selectedKey={customer.CustomerSectorID}
                            required={true}
                            onChange={(_, option, index) => {
                                changeHandlers.changeDropdown(option, 'CustomerSectorID', index);

                                var selectedCustomerSector = lookupData.CustomerSectors.find(s => s.ID === option.key);

                                setSelectedSystemSectorID(selectedCustomerSector?.['SystemSectorID']);
                            }}
                            errorMessage={errors.CustomerSectorID}
                        />

                        <CrDropdown
                            label='System Sector'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Sectors)}
                            selectedKey={selectedSystemSectorID}
                            disabled={true}
                        />

                        <CrDropdown
                            label='Edition'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Editions)}
                            selectedKey={customer.EditionID}
                            onChange={(_, option, index) => {
                                changeHandlers.changeDropdown(option, 'EditionID', index);
                                setSelectedEditionID(option.key);
                            }}
                            errorMessage={errors.EditionID}
                            required={true}
                        />

                        <CrDropdown
                            label='Customer Country'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.CustomerCountries)}
                            selectedKey={customer.CustomerCountryID}
                            required={true}
                            onChange={(_, option, index) => {
                                changeHandlers.changeDropdown(option, 'CustomerCountryID', index);

                                var selectedCustomerCountry = lookupData.CustomerCountries.find(c => c.ID === option.key);

                                setSelectedSystemCountryID(selectedCustomerCountry?.['SystemCountryID']);
                            }}
                            errorMessage={errors.CustomerCountryID}
                        />

                        {
                            defOrgConfigs.length > 0 &&
                            selectedEditionID &&
                            selectedSystemSectorID &&
                            selectedSystemCountryID &&

                            <CrDropdown
                                label='Org Config'
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(defOrgConfigs)}
                                selectedKey={customer.OrgConfigID}
                                onChange={(_, option, index) =>
                                    changeHandlers.changeDropdown(option, 'OrgConfigID', index)
                                }
                                required={true}
                                errorMessage={errors.OrgConfigID}
                            />
                        }

                        {/* =========================
                            PRIMARY USER
                        ========================= */}

                        <CrDropdown
                            label='Primary User Title'
                            className={styles.formField}
                            options={[
                                { key: 'Mr', text: 'Mr' },
                                { key: 'Mrs', text: 'Mrs' },
                                { key: 'Ms', text: 'Ms' },
                                { key: 'Dr', text: 'Dr' }
                            ]}
                            selectedKey={customer.PrimaryUserTitle}
                            onChange={(_, option, index) =>
                                changeHandlers.changeDropdown(option, 'PrimaryUserTitle', index)
                            }
                            disabled={customer.ID > 0}
                            required={true}
                            errorMessage={errors.PrimaryUserTitle}
                        />

                        <CrTextField
                            label="Primary User Firstname"
                            maxLength={250}
                            className={styles.formField}
                            value={customer.PrimaryUserFirstname}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'PrimaryUserFirstname')}
                            disabled={customer.ID > 0}
                            required={true}
                            errorMessage={errors.PrimaryUserFirstname}
                        />

                        <CrTextField
                            label="Primary User Surname"
                            maxLength={250}
                            className={styles.formField}
                            value={customer.PrimaryUserSurname}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'PrimaryUserSurname')}
                            disabled={customer.ID > 0}
                            required={true}
                            errorMessage={errors.PrimaryUserSurname}
                        />

                        <CrTextField
                            label="Primary User Email"
                            maxLength={500}
                            className={styles.formField}
                            value={customer.PrimaryUserEmail}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'PrimaryUserEmail')}
                            disabled={customer.ID > 0}
                            required={true}
                            errorMessage={errors.PrimaryUserEmail}
                        />

                        <CrTextField
                            label="Primary User Phone"
                            maxLength={50}
                            className={styles.formField}
                            value={customer.PrimaryUserPhone}
                            onChange={(_, value) => changeHandlers.changeTextField(value, 'PrimaryUserPhone')}
                            disabled={customer.ID > 0}
                        />

                        {/* =========================
                            SERVERS
                        ========================= */}

                        <CrDropdown
                            label='Server for customers TEST environment'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Servers)}
                            selectedKey={customer.TestServerID}
                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'TestServerID', index)}
                            disabled={customer.ID > 0}
                            errorMessage={errors.TestServerID}
                        />

                        <CrDropdown
                            label='Server for customers LIVE environment'
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Servers)}
                            selectedKey={customer.LiveServerID}
                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'LiveServerID', index)}
                            disabled={customer.ID > 0 && customerBeforeChanges.LiveServerID != null}
                        />

                        {/* =========================
                            APPS
                        ========================= */}

                        <CrDropdown
                            label="Apps licenced for use in the Live environment"
                            multiSelect
                            className={styles.formField}
                            options={
                                availableApps.length > 0
                                    ? LookupService.entitiesToSelectableOptions(availableApps)
                                    : []
                            }
                            selectedKeys={customer.CustomerApps && customer.CustomerApps?.map((x) => { return x.AppID; })}
                            onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'CustomerApps', new CustomerApp(), 'AppID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.customerService.read(id, true, true)}
            onAfterLoad={customer => {
                setSelectedEditionID(customer.EditionID);
                setSelectedSystemSectorID(customer.SystemSectorID);
                setSelectedSystemCountryID(customer.SystemCountryID);
            }}
            loadNewEntity={() => new Customer()}
            loadEntityValidations={() => new CustomerValidations()}
            onValidateEntity={validateEntity}
            onCreate={g => dataServices.customerService.create(g)}
            onUpdate={g => dataServices.customerService.update(g.ID, g)}

            parentEntities={dataServices.customerService.parentEntities}
            includePropertiesOnSave={['CustomerApps']}
        />
    );
};